import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { applications } from '../../utils/api';
import './ADIForm.css';

/* ─── Step components ─── */
import Step1Personal from './Step1Personal';
import Step2ADILicence from './Step2ADILicence';
import Step3DrivingLicence from './Step3DrivingLicence';
import Step4Vehicle from './Step4Vehicle';
import Step5ServiceDeclarations from './Step5ServiceDeclarations';

const STEPS = [
  { number: 1, label: 'Personal Details' },
  { number: 2, label: 'ADI Licence' },
  { number: 3, label: 'Driving Licence' },
  { number: 4, label: 'Vehicle & Docs' },
  { number: 5, label: 'Service & Sign' },
];

function ADIFormContainer() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(() => ({
    email: sessionStorage.getItem('instructorPreEmail') || '',
  }));
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Guard: must have gone through email verification first
  useEffect(() => {
    const token = sessionStorage.getItem('instructorRegToken');
    if (!token) {
      navigate('/apply-signup', { replace: true });
    }
  }, [navigate]);

  // Clear all errors whenever the user moves to a new step
  useEffect(() => {
    setErrors({});
  }, [currentStep]);

  const updateField = (name, value) =>
    setFormData(prev => ({ ...prev, [name]: value }));

  const updateFile = (name, file) =>
    setFiles(prev => ({ ...prev, [name]: file }));

  const clearError = (name) =>
    setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });

  /* ─── Per-step validation ─── */
  const validate = (step) => {
    const e = {};

    if (step === 1) {
      if (!formData.full_name?.trim()) {
        e.full_name = 'Full name is required';
      } else {
        const nm = formData.full_name.trim();
        if (nm.length < 2) e.full_name = 'Full name is too short — please enter your full name';
        else if (nm.length > 80) e.full_name = 'Full name must be 80 characters or fewer';
        else if (/\d/.test(nm)) e.full_name = 'Full name must not contain numbers';
        else if (/[^A-Za-z\s\u002D\u0027\u2019\u00C0-\u017F]/.test(nm))
          e.full_name = 'Full name may only contain letters, spaces, hyphens, and apostrophes';
      }

      // Date of birth — required, must be 18–80 years old
      if (!formData.date_of_birth) {
        e.date_of_birth = 'Date of birth is required';
      } else {
        const dob = new Date(formData.date_of_birth);
        const today = new Date();
        const age = today.getFullYear() - dob.getFullYear()
          - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
        if (age < 18) e.date_of_birth = 'You must be at least 18 years old';
        else if (age > 80) e.date_of_birth = 'Please enter a valid date of birth';
      }

      // Phone — UK mobile or landline
      if (!formData.phone?.trim()) {
        e.phone = 'Phone number is required';
      } else {
        const ph = formData.phone.replace(/[\s\-()+.]/g, '');
        if (!/^(\+44\d{10}|0[1-9]\d{8,9})$/.test(ph))
          e.phone = 'Enter a valid UK phone number (e.g. 07911 123456)';
      }

      // Email
      if (!formData.email?.trim()) {
        e.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email)) {
        e.email = 'Enter a valid email address';
      }

      // NI number — optional, validated if entered
      if (formData.ni_number?.trim()) {
        const ni = formData.ni_number.replace(/\s/g, '').toUpperCase();
        if (!/^[A-Z]{2}\d{6}[A-D]$/.test(ni))
          e.ni_number = 'Enter a valid NI number (e.g. AB 12 34 56 C)';
      }

      if (!formData.address_line1?.trim()) e.address_line1 = 'Address is required';
      if (!formData.city?.trim()) e.city = 'City / Town is required';
      if (!formData.postcode?.trim()) {
        e.postcode = 'Postcode is required';
      } else {
        const pc = formData.postcode.replace(/\s/g, '').toUpperCase();
        if (!/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(pc))
          e.postcode = 'Enter a valid UK postcode (e.g. SW1A 1AA)';
      }
    }

    if (step === 2) {
      // PRN — exactly 6 digits
      if (!formData.prn?.trim()) {
        e.prn = 'PRN is required';
      } else if (!/^\d{6}$/.test(formData.prn.trim())) {
        e.prn = 'PRN must be exactly 6 digits';
      }
      // Badge expiry — required, must be future, and within 4 years (ADI badge validity)
      if (!formData.badge_expiry) {
        e.badge_expiry = 'Badge expiry date is required';
      } else {
        const expiry = new Date(formData.badge_expiry);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const maxExpiry = new Date(today); maxExpiry.setFullYear(maxExpiry.getFullYear() + 4);
        if (expiry < today)
          e.badge_expiry = 'Your ADI badge must not be expired — enter the expiry date shown on your badge';
        else if (expiry > maxExpiry)
          e.badge_expiry = 'Check date — ADI badges are valid for up to 4 years from issue';
      }
      if (!files.green_badge_front) e.green_badge_front = 'Green badge front photo is required';
      if (!files.green_badge_back) e.green_badge_back = 'Green badge back photo is required';
    }

    if (step === 3) {
      // UK driving licence — exactly 16 alphanumeric chars in correct format
      if (!formData.licence_number?.trim()) {
        e.licence_number = 'Licence number is required';
      } else {
        const ln = formData.licence_number.replace(/\s/g, '').toUpperCase();
        if (ln.length !== 16) {
          e.licence_number = 'UK driving licence number must be exactly 16 characters';
        } else if (!/^[A-Z9]{5}\d{6}[A-Z9]{2}[A-Z0-9]{2}[A-Z]$/.test(ln)) {
          e.licence_number = 'Invalid format — check your photocard licence (e.g. SMITH731021AB9XY)';
        }
      }
      if (!files.driving_licence_front) e.driving_licence_front = 'Licence front photo is required';
      if (!files.driving_licence_back) e.driving_licence_back = 'Licence back photo is required';

      // DVLA check code — required, exactly 8 alphanumeric chars
      if (!formData.dvla_check_code?.trim()) {
        e.dvla_check_code = 'DVLA check code is required — generate it at gov.uk/view-driving-licence';
      } else {
        const code = formData.dvla_check_code.trim().replace(/\s/g, '');
        if (code.length !== 8)
          e.dvla_check_code = 'DVLA check code must be exactly 8 characters (e.g. 44gxXYrz)';
        else if (!/^[A-Z0-9]{8}$/i.test(code))
          e.dvla_check_code = 'DVLA check code can only contain letters and numbers';
      }

      // Licence issue date — optional but validated if provided
      if (formData.licence_issue_date) {
        const issued = new Date(formData.licence_issue_date);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const minDate = new Date('1967-01-01');
        if (issued > today)
          e.licence_issue_date = 'Licence issue date cannot be in the future';
        else if (issued < minDate)
          e.licence_issue_date = 'Enter a valid issue date — UK driving licences were introduced in 1967';
      }
    }

    if (step === 4) {
      if (!formData.vehicle_make?.trim()) e.vehicle_make = 'Vehicle make is required';
      if (!formData.vehicle_model?.trim()) e.vehicle_model = 'Vehicle model is required';
      // UK vehicle registration — modern format AB12 CDE
      if (!formData.vehicle_reg?.trim()) {
        e.vehicle_reg = 'Registration number is required';
      } else {
        const reg = formData.vehicle_reg.replace(/\s/g, '').toUpperCase();
        if (!/^[A-Z]{2}\d{2}[A-Z]{3}$/.test(reg))
          e.vehicle_reg = 'Enter a valid UK registration (e.g. AB12 CDE)';
      }
      // Vehicle year — 4-digit, 2000 to current year + 1
      if (!formData.vehicle_year?.trim()) {
        e.vehicle_year = 'Vehicle year is required';
      } else {
        const yr = parseInt(formData.vehicle_year, 10);
        const maxYear = new Date().getFullYear() + 1;
        if (isNaN(yr) || formData.vehicle_year.trim().length !== 4 || yr < 2000 || yr > maxYear)
          e.vehicle_year = `Enter a valid 4-digit year (2000 – ${maxYear})`;
      }
      if (!formData.vehicle_colour?.trim()) e.vehicle_colour = 'Vehicle colour is required';
      if (!formData.transmission) e.transmission = 'Transmission type is required';
      if (!files.insurance_doc) e.insurance_doc = 'Insurance certificate is required';
      // MOT required only for vehicles 3+ years old
      const vrY = parseInt(formData.vehicle_year, 10);
      const vAge = !isNaN(vrY) && formData.vehicle_year?.trim().length === 4 ? new Date().getFullYear() - vrY : null;
      if (vAge !== null && vAge >= 3 && !files.mot_doc) {
        e.mot_doc = 'MOT certificate is required for vehicles 3 or more years old';
      }
      const extPhotos = files.vehicle_exterior_photos;
      if (!extPhotos || (Array.isArray(extPhotos) && extPhotos.length === 0))
        e.vehicle_exterior_photos = 'At least one exterior photo is required';
      const intPhotos = files.vehicle_interior_photos;
      if (!intPhotos || (Array.isArray(intPhotos) && intPhotos.length === 0))
        e.vehicle_interior_photos = 'At least one interior photo is required';
      const dualPhotos = files.dual_controls_photos;
      if (!dualPhotos || (Array.isArray(dualPhotos) && dualPhotos.length === 0))
        e.dual_controls_photos = 'At least one dual controls photo is required';
    }

    if (step === 5) {
      if (!formData.test_centres || formData.test_centres.length === 0)
        e.test_centres = 'Select at least one test centre area';
      if (formData.referred_by_instructor === 'yes' && formData.referring_instructor_adi?.trim()) {
        if (!/^\d{6}$/.test(formData.referring_instructor_adi.trim()))
          e.referring_instructor_adi = 'Referring instructor ADI / PRN must be exactly 6 digits';
      }
      // Referral code — required when referred, with format check
      if (formData.referred_by_instructor === 'yes') {
        if (!formData.referral_code_used?.trim()) {
          e.referral_code_used = 'Referral code is required — ask your referring instructor for their unique code';
        } else {
          const rc = formData.referral_code_used.trim().toUpperCase();
          if (!/^((FT|ADI)-[A-Z0-9]{4,8}|[A-Z0-9]{6})$/.test(rc))
            e.referral_code_used = 'Enter a valid code: FT-XXXXXX, ADI-XXXXXX, or a 6-character legacy code';
        }
      }
      if (!formData.account_holder_name?.trim()) e.account_holder_name = 'Account holder name is required';
      if (!formData.bank_name?.trim()) e.bank_name = 'Bank name is required';
      // Sort code — must be XX-XX-XX
      if (!formData.sort_code?.trim()) {
        e.sort_code = 'Sort code is required';
      } else if (!/^\d{2}-\d{2}-\d{2}$/.test(formData.sort_code)) {
        e.sort_code = 'Sort code must be in format XX-XX-XX (e.g. 12-34-56)';
      }
      // Account number — exactly 8 digits
      if (!formData.account_number?.trim()) {
        e.account_number = 'Account number is required';
      } else if (!/^\d{8}$/.test(formData.account_number)) {
        e.account_number = 'Account number must be exactly 8 digits';
      }
      if (!formData.declaration_bank) e.declaration_bank = 'Please confirm your bank details';
      if (!formData.declaration_adi) e.declaration_adi = 'Please confirm DVSA registration';
      if (!formData.declaration_vehicle) e.declaration_vehicle = 'Please confirm vehicle compliance';
      if (!formData.declaration_terms) e.declaration_terms = 'You must agree to the terms';
      if (!formData.signature_data) e.signature_data = 'Please draw your signature in the box below';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (validate(currentStep)) {
      setErrors({});
      setCurrentStep(s => Math.min(s + 1, 5));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goPrev = () => {
    setErrors({});
    setCurrentStep(s => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!validate(5)) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      // Include pre-registration token so backend links form to verified account
      const regToken = sessionStorage.getItem('instructorRegToken');
      if (regToken) fd.append('registration_token', regToken);
      Object.entries(formData).forEach(([k, v]) => {
        if (v !== null && v !== undefined) {
          if (Array.isArray(v)) v.forEach(item => fd.append(k + '[]', item));
          else fd.append(k, v);
        }
      });
      Object.entries(files).forEach(([k, f]) => {
        if (!f) return;
        if (Array.isArray(f)) f.forEach(file => fd.append(k, file));
        else fd.append(k, f);
      });
      const result = await applications.submit(fd);
      // Clean up pre-registration session data now that application is submitted
      sessionStorage.removeItem('instructorRegToken');
      sessionStorage.removeItem('instructorPreEmail');
      navigate('/application-submitted', {
        replace: true,
        state: {
          applicationId: result?.data?.applicationNumber || result?.data?.applicationId,
          email: formData.email
        }
      });
    } catch (err) {
      console.error('Submission error:', err);
      const isSessionError = err.status === 401 ||
        /token|session|expired|unauthori[zs]ed/i.test(err.message || '');
      setErrors({
        submit: isSessionError
          ? 'Your session has expired. Please go back to the sign-in page and log in again — your form data will need to be re-entered.'
          : (err.message || 'Submission failed. Please try again.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const setError = (name, msg) => setErrors(prev => ({ ...prev, [name]: msg }));
  const stepProps = { formData, files, errors, updateField, updateFile, clearError, setError };
  const handleKeyDown = (e) => {
  if (e.key === 'Enter' && currentStep < 5) {
    e.preventDefault();
  }
};

  return (
    <div className="adi-container">
      {/* Page header */}
      <div className="adi-page-header">
        <div className="adi-header-inner">
          <span className="adi-eyebrow">Fast Track Driving School</span>
          <h1>ADI Instructor Registration Form</h1>
          <p>Join our nationwide network of DVSA-approved instructors. Complete all 5 steps — it takes around 15 minutes.</p>
        </div>
      </div>

      {/* Progress stepper */}
      <div className="adi-stepper-wrap">
        <div className="adi-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.number}>
              <div className={`adi-step${currentStep === s.number ? ' adi-step--active' : ''}${currentStep > s.number ? ' adi-step--done' : ''}`}>
                <div className="adi-step-circle">
                  {currentStep > s.number ? '✓' : s.number}
                </div>
                <span className="adi-step-label">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`adi-step-line${currentStep > s.number ? ' adi-step-line--done' : ''}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
        
      </div>

      {/* Form body */}
      <div className="adi-form-body">
<form onSubmit={handleSubmit} onKeyDown={handleKeyDown} encType="multipart/form-data" noValidate>

          {currentStep === 1 && <Step1Personal {...stepProps} />}
          {currentStep === 2 && <Step2ADILicence {...stepProps} />}
          {currentStep === 3 && <Step3DrivingLicence {...stepProps} />}
          {currentStep === 4 && <Step4Vehicle {...stepProps} />}
          {currentStep === 5 && <Step5ServiceDeclarations {...stepProps} />}

          {errors.submit && (
            <div className="adi-submit-error">{errors.submit}</div>
          )}

          {/* Navigation */}
          <div className="adi-nav-btns">
            {currentStep > 1 && (
              <button type="button" className="adi-btn adi-btn--back" onClick={goPrev}>
                ← Back
              </button>
            )}
            {currentStep < 5 ? (
              <button type="button" className="adi-btn adi-btn--next" onClick={goNext}>
                Continue →
              </button>
            ) : (
              <button type="button" className="adi-btn adi-btn--submit" disabled={submitting} onClick={handleSubmit}>
                {submitting ? 'Submitting…' : 'Submit Application ✓'}
              </button>
            )}
          </div>

          <p className="adi-step-indicator">Step {currentStep} of {STEPS.length}</p>
        </form>
      </div>
    </div>
  );
}

export default ADIFormContainer;
